// Filesystem plugin base class and shared types.
// Each plugin is a different file access method (OPFS, disk, cloud, etc.).
/**
 * Abstract base class for filesystem plugins.
 * Hooks have safe defaults; subclasses override only what they need.
 */
export class FilesystemPlugin {
    constructor(adapter) {
        this.adapter = adapter;
    }
    /** Get the underlying filesystem adapter */
    getAdapter() {
        return this.adapter;
    }
    /** Whether this plugin supports saving files */
    canSave() {
        return true;
    }
    /** Whether this plugin supports uploading files and folders */
    supportsUpload() {
        return true;
    }
    /** Whether this plugin supports creating new folders */
    supportsNewFolder() {
        return true;
    }
    /** Whether this plugin supports creating new empty files */
    supportsNewFile() {
        return true;
    }
    /** Whether this plugin requires user permission/authentication */
    requiresPermission() {
        return false;
    }
    /**
     * Called when plugin is activated (user switches to this context)
     * @returns true if activation successful, false if failed
     */
    async onActivate() {
        return true;
    }
    /**
     * Called when plugin is deactivated (user switches away)
     */
    async onDeactivate() {
        // Default: no cleanup needed
    }
    /**
     * Show plugin-specific setup UI (e.g., folder picker, login dialog)
     * @returns true if setup completed, false if cancelled
     */
    async showSetupUI(options) {
        return true;
    }
    /**
     * Check if plugin is ready to use (has directory selected, authenticated, etc.)
     */
    async isReady() {
        return true;
    }
    /**
     * Get the current root path for this plugin's context
     */
    getDefaultPath() {
        return '/';
    }
    /**
     * Whether this plugin can be closed (e.g., disconnect folder access)
     */
    canClose() {
        return false;
    }
    /**
     * Close/disconnect this plugin's access (e.g., clear folder handle)
     * Only called if canClose() returns true
     */
    async close() {
        // Default: no-op
    }
    /**
     * Update UI elements specific to this plugin's state
     */
    async updateUI(uiCallbacks) {
        uiCallbacks.hideOpenFolderUI();
        uiCallbacks.showPermissionBanner(false);
        uiCallbacks.hideUnsupportedBrowserUI();
        uiCallbacks.hidePluginMessage();
    }
    /**
     * Get title bar menu items for this plugin
     */
    getTitleBarMenuItems() {
        return [];
    }
    supportsFileContextAction(action, target) {
        switch (action) {
            case 'open':
            case 'open-new-tab':
                return !target.isDir;
            case 'open-in-script-editor':
                return !target.isDir && target.name.endsWith('.py');
            case 'download':
                return !target.isDir;
            case 'rename':
            case 'delete':
                return true;
            default:
                return false;
        }
    }
    /**
     * Trigger a redraw of the title bar buttons for this plugin
     */
    redrawTitleBarButtons() {
        window.dispatchEvent(new CustomEvent('pluginTitleBarRedraw', {
            detail: { pluginId: this.getId() }
        }));
    }
    /** Whether the file dialog should show a manual refresh button. */
    showsManualRefreshButton() {
        return true;
    }
    /** Whether this plugin should be visible in editor UI surfaces. */
    isVisibleInUI() {
        return true;
    }
    /**
     * Whether this plugin supports opening files via a file picker dialog
     * If false, files can only be opened via the Files view context menu
     */
    supportsOpenFilePicker() {
        return false;
    }
    /**
     * Whether this plugin supports Save As via a file picker dialog
     * If false, only Save (to existing path) is supported
     */
    supportsSaveAsFilePicker() {
        return false;
    }
    /**
     * Whether this plugin handles Save As internally (bypassing writeFile).
     * When true, the file dialog calls handleSaveAs(name) instead of
     * adapter.writeFile(path, content).
     */
    get interceptsSaveAs() {
        return false;
    }
    /**
     * Plugin-specific Save As handler, called instead of adapter.writeFile
     * when interceptsSaveAs is true.
     */
    async handleSaveAs(_name) {
        return false;
    }
    /**
     * Plugin-specific open handler for paths that cannot be read through the
     * generic adapter.readFile pipeline.
     * Return true when the plugin fully handled the open.
     */
    async handleOpenPath(_path) {
        return false;
    }
    /**
     * Refresh plugin-owned data (catalog, indexes) before Save or seed.
     */
    async prepareToSave() {
        // Default: nothing extra to prepare
    }
    async prepareToSeed() {
        await this.prepareToSave();
    }
    /**
     * Whether `additionalGlyphCount` new glyphs may be added under this backend.
     */
    async canAddGlyphs(additionalGlyphCount) {
        void additionalGlyphCount;
        return { allowed: true };
    }
    /**
     * Sync view of {@link canAddGlyphs} for model mutations that cannot await.
     * Cloud uses live glyph count vs the last known owner cap.
     */
    getCachedCanAddGlyphs(additionalGlyphCount) {
        void additionalGlyphCount;
        return { allowed: true };
    }
    /**
     * Whether masters may be added, removed, or reinterpolated. Those edits
     * rewrite a layer on every glyph, so every glyph must be resident.
     */
    getMasterTopologyGate() {
        return { allowed: true };
    }
    /**
     * Sync admission for a live Yjs commit. Cloud rejects oversize packets
     * or shards before the change-bridge emits. Local backends allow.
     */
    canSubmitCollabUpdate(requests) {
        void requests;
        return { allowed: true };
    }
    notifyCollabSubmitRejected(_decision) { }
    /**
     * Return a copy of font JSON without this plugin's owned extra data.
     * Used when Save As targets a different plugin.
     */
    stripOwnedFontData(fontJson) {
        return fontJson;
    }
    /**
     * Open a file picker dialog and return the selected file path
     * Only called if supportsOpenFilePicker() returns true
     */
    async showOpenFilePicker(_options) {
        return null;
    }
    /**
     * Show a save file picker dialog and return the selected file path
     * Only called if supportsSaveAsFilePicker() returns true
     */
    async showSaveFilePicker(_options) {
        return null;
    }
}
